/**
 * Intelligence Layer
 * Detects correlations, generates insights, and orchestrates
 * cross-system intelligence for Therappi
 */

import { supabase } from '@/integrations/supabase/client';
import type { Level } from './fiveLevelFramework';
import { getLevelsForFeature } from './fiveLevelFramework';

export interface Correlation {
  id: string;
  user_id: string;
  metric_a: string;
  metric_b: string;
  correlation_strength: number; // -1 to 1
  correlation_type: 'positive' | 'negative' | 'neutral';
  sample_size: number;
  confidence: number; // 0-100
  detected_at: Date;
  insight_text: string;
}

export interface GeneratedInsight {
  id: string;
  user_id: string;
  insight_text: string;
  insight_type: 'pattern' | 'correlation' | 'recommendation' | 'timeline' | 'level';
  priority: 'low' | 'medium' | 'high' | 'urgent';
  related_levels: Level[];
  action_items: string[];
  created_at: Date;
  expires_at?: Date;
}

/**
 * Detect correlations between two metrics
 */
export async function detectCorrelation(
  userId: string,
  metricA: string,
  metricB: string,
  days: number = 30
): Promise<Correlation | null> {
  try {
    // This would fetch time-series data for both metrics
    // For now, returning structure
    
    const correlationStrength = 0; // Calculate using Pearson correlation
    const confidence = 0; // Based on sample size and p-value
    
    if (Math.abs(correlationStrength) < 0.3) {
      return null; // Too weak to report
    }

    const insight = generateCorrelationInsight(metricA, metricB, correlationStrength);

    return {
      id: `corr-${Date.now()}`,
      user_id: userId,
      metric_a: metricA,
      metric_b: metricB,
      correlation_strength: correlationStrength,
      correlation_type: correlationStrength > 0 ? 'positive' : 'negative',
      sample_size: days,
      confidence,
      detected_at: new Date(),
      insight_text: insight
    };
  } catch {
    // Error detecting correlation - handled silently
    return null;
  }
}

/**
 * Generate insight text for a correlation
 */
function generateCorrelationInsight(
  metricA: string,
  metricB: string,
  strength: number
): string {
  const direction = strength > 0 ? 'higher' : 'lower';
  const magnitude = Math.abs(strength) > 0.7 ? 'strongly' : 'moderately';
  
  return `When your ${metricA} is ${direction}, your ${metricB} tends to be ${magnitude} affected.`;
}

/**
 * Detect all correlations for a user
 */
export async function detectAllCorrelations(userId: string): Promise<Correlation[]> {
  const correlations: Correlation[] = [];

  // Define metric pairs to check
  const metricPairs = [
    ['il_score', 'mood'],
    ['sleep_quality', 'energy'],
    ['cycle_day', 'mood'],
    ['stress', 'il_score'],
    ['whoop_recovery', 'energy'],
    ['nutrition_score', 'mood'],
    ['meditation_frequency', 'mental_wellness'],
    ['exercise', 'whoop_recovery']
  ];

  for (const [metricA, metricB] of metricPairs) {
    const correlation = await detectCorrelation(userId, metricA, metricB);
    if (correlation) {
      correlations.push(correlation);
    }
  }

  return correlations;
}

/**
 * Generate insights based on current state
 */
export async function generateInsights(userId: string): Promise<GeneratedInsight[]> {
  const insights: GeneratedInsight[] = [];

  try {
    // Fetch recent data
    const today = new Date().toISOString().split('T')[0];
    
    const { data: todayCheckin } = await supabase
      .from('daily_checkins')
      .select('*')
      .eq('user_id', userId)
      .eq('date', today)
      .single();

    const { data: ilData } = await supabase
      .from('il_daily')
      .select('*')
      .eq('user_id', userId)
      .order('date', { ascending: false })
      .limit(7);

    const { data: mentalWellness } = await supabase
      .from('mental_wellness_daily' as any)
      .select('*')
      .eq('user_id', userId)
      .order('date', { ascending: false })
      .limit(7);

    // Insight 1: IL Score trend
    if (ilData && ilData.length >= 3) {
      const trend = calculateTrend(ilData.map(d => d.il_score));
      if (trend === 'increasing' && ilData[0].il_score > 60) {
        insights.push({
          id: `insight-il-${Date.now()}`,
          user_id: userId,
          insight_text: 'Your inflammation has been rising. Time to review what changed.',
          insight_type: 'pattern',
          priority: 'high',
          related_levels: ['physical', 'emotional'],
          action_items: [
            'Check top IL contributors',
            'Review recent stress',
            'Ensure adequate sleep'
          ],
          created_at: new Date()
        });
      }
    }

    // Insight 2: Mental wellness pattern
    if (mentalWellness && mentalWellness.length >= 3) {
      const avgScore = mentalWellness.reduce((sum: number, d: any) => sum + (d.mental_wellness_score || 0), 0) / mentalWellness.length;
      if (avgScore < 50) {
        insights.push({
          id: `insight-mw-${Date.now()}`,
          user_id: userId,
          insight_text: 'Your mental wellness has been below 50. Consider using coping tools.',
          insight_type: 'recommendation',
          priority: 'high',
          related_levels: ['mental', 'emotional'],
          action_items: [
            'Try a coping tool',
            'Check in with Therappi AI',
            'Review thought patterns'
          ],
          created_at: new Date()
        });
      }
    }

    // Insight 3: Cycle + Mood
    if (todayCheckin?.menstrual_cycle_day && todayCheckin.menstrual_cycle_day > 20) {
      if (todayCheckin.daily_feeling === 'struggling') {
        insights.push({
          id: `insight-cycle-${Date.now()}`,
          user_id: userId,
          insight_text: `You're in the luteal phase (Day ${todayCheckin.menstrual_cycle_day}) and mood is low. This is common.`,
          insight_type: 'pattern',
          priority: 'medium',
          related_levels: ['physical', 'emotional'],
          action_items: [
            'Be gentle with yourself',
            'Reduce stimulation',
            'Use emotional regulation tools'
          ],
          created_at: new Date()
        });
      }
    }

    // Insight 4: WHOOP recovery
    const { data: whoopData } = await supabase
      .from('whoop_recovery_data')
      .select('*')
      .eq('user_id', userId)
      .order('recorded_at', { ascending: false })
      .limit(1)
      .single();

    if (whoopData && whoopData.recovery_score < 40) {
      insights.push({
        id: `insight-whoop-${Date.now()}`,
        user_id: userId,
        insight_text: `Your recovery is at ${whoopData.recovery_score}%. Prioritise rest today.`,
        insight_type: 'recommendation',
        priority: 'high',
        related_levels: ['physical', 'emotional'],
        action_items: [
          'Reduce training intensity',
          'Focus on sleep',
          'Consider meditation for nervous system reset'
        ],
        created_at: new Date()
      });
    }

  } catch {
    // Error generating insights - handled silently
  }

  return insights;
}

/**
 * Calculate trend from array of numbers
 */
function calculateTrend(values: number[]): 'increasing' | 'decreasing' | 'stable' {
  if (values.length < 2) return 'stable';
  
  const recent = values.slice(0, Math.ceil(values.length / 2));
  const older = values.slice(Math.ceil(values.length / 2));
  
  const recentAvg = recent.reduce((a, b) => a + b, 0) / recent.length;
  const olderAvg = older.reduce((a, b) => a + b, 0) / older.length;
  
  const change = ((recentAvg - olderAvg) / olderAvg) * 100;
  
  if (change > 10) return 'increasing';
  if (change < -10) return 'decreasing';
  return 'stable';
}

/**
 * Get top priority insight for dashboard
 */
export async function getTopInsight(userId: string): Promise<GeneratedInsight | null> {
  const insights = await generateInsights(userId);
  
  if (insights.length === 0) return null;
  
  // Sort by priority
  const priorityOrder = { urgent: 4, high: 3, medium: 2, low: 1 };
  insights.sort((a, b) => priorityOrder[b.priority] - priorityOrder[a.priority]);
  
  return insights[0];
}

/**
 * Get recommended actions based on current state
 */
export async function getRecommendedActions(
  userId: string,
  userType: 'performance_optimizer' | 'spiritual_seeker' | 'therapy_first' | 'holistic'
): Promise<string[]> {
  const insights = await generateInsights(userId);
  const actions: string[] = [];

  // Collect all action items
  insights.forEach(insight => {
    actions.push(...insight.action_items);
  });

  // Filter by user type
  const filteredActions = actions.filter(action => {
    if (userType === 'performance_optimizer') {
      return action.includes('recovery') || action.includes('training') || action.includes('IL');
    }
    if (userType === 'spiritual_seeker') {
      return action.includes('meditation') || action.includes('energy') || action.includes('Future Self');
    }
    if (userType === 'therapy_first') {
      return action.includes('coping') || action.includes('Therappi') || action.includes('patterns');
    }
    return true; // holistic sees all
  });

  // Return top 3 unique actions
  return [...new Set(filteredActions)].slice(0, 3);
}
